import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import GradeCardService from "../../../_services/GradeCardService";
import ItemInputFactory from "./ItemInputFactory";

interface Props {
  enquiryId: number;
  enrollmentId?: number;
  studentName: string;
  structureId: number;
  termIndex: number;
  yearId?: number;
  structure: any; // Full structure tree with sections > items
  onBack: () => void;
  onSaved: () => void;
}

/**
 * Full grade card form for a single student.
 * Shows all sections and items with appropriate input controls.
 */
const PerStudentForm: React.FC<Props> = ({
  enquiryId,
  enrollmentId,
  studentName,
  structureId,
  termIndex,
  yearId,
  structure,
  onBack,
  onSaved,
}) => {
  const [values, setValues] = useState<Record<number, string | null>>({});
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cardStatus, setCardStatus] = useState<string>("empty");

  useEffect(() => {
    loadExistingEntries();
  }, [enquiryId, structureId, termIndex]);

  const loadExistingEntries = async () => {
    setLoading(true);
    try {
      const res = await GradeCardService.getStudentEntries(enquiryId, {
        structureId,
        yearId,
      });
      const data = res.data?.data;
      if (data) {
        // Find entries for current term
        const currentCard = (data.cards || []).find(
          (c: any) => c.TermIndex === termIndex,
        );
        if (currentCard) {
          setCardStatus(currentCard.Status || "draft");
          setRemarks(currentCard.Remarks || "");
        }
        const entryMap: Record<number, string | null> = {};
        for (const entry of data.entries || []) {
          if (entry.TermIndex === termIndex) {
            entryMap[entry.ItemId] = entry.Value;
          }
        }
        setValues(entryMap);
      }
    } catch (e) {
      console.error("Failed to load entries", e);
    } finally {
      setLoading(false);
    }
  };

  const resolveRatingScale = (item: any, section: any) => {
    // Item override > Section override > Structure default
    if (item.RatingScaleOptions) {
      return typeof item.RatingScaleOptions === "string"
        ? JSON.parse(item.RatingScaleOptions)
        : item.RatingScaleOptions;
    }
    if (section.RatingScaleOptions) {
      return typeof section.RatingScaleOptions === "string"
        ? JSON.parse(section.RatingScaleOptions)
        : section.RatingScaleOptions;
    }
    if (structure.DefaultRatingScaleOptions) {
      return typeof structure.DefaultRatingScaleOptions === "string"
        ? JSON.parse(structure.DefaultRatingScaleOptions)
        : structure.DefaultRatingScaleOptions;
    }
    return [];
  };

  const handleSave = async (publish = false) => {
    if (publish) setPublishing(true);
    else setSaving(true);

    try {
      const entries = Object.entries(values).map(([itemId, value]) => ({
        itemId: Number(itemId),
        value,
      }));

      await GradeCardService.saveEntries({
        enquiryId,
        enrollmentId,
        structureId,
        termIndex,
        yearId,
        remarks,
        entries,
      });

      if (publish) {
        await GradeCardService.publishGradeCard({
          enquiryId,
          structureId,
          termIndex,
          yearId,
        });
      }

      onSaved();
    } catch (e: any) {
      alert(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading student data...</div>;
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          padding: "12px 16px",
          background: "#f0f4ff",
          borderRadius: 8,
        }}
      >
        <div>
          <Button variant="secondary" size="sm" onClick={onBack} className="me-3">
            ← Back
          </Button>
          <strong style={{ fontSize: 16 }}>{studentName}</strong>
          {cardStatus !== "empty" && (
            <span
              style={{
                marginLeft: 12,
                padding: "2px 8px",
                fontSize: 11,
                borderRadius: 4,
                background:
                  cardStatus === "published" ? "#d4edda" : "#fff3cd",
                color: cardStatus === "published" ? "#155724" : "#856404",
              }}
            >
              {cardStatus}
            </span>
          )}
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            className="me-2"
            onClick={() => handleSave(false)}
            disabled={saving || publishing}
          >
            {saving ? "Saving..." : "Save Draft"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleSave(true)}
            disabled={saving || publishing}
          >
            {publishing ? "Publishing..." : "Save & Publish"}
          </Button>
        </div>
      </div>

      {(structure.sections || []).map((section: any) => (
        <div
          key={section.Id}
          style={{
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            marginBottom: 16,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: "var(--surface-sunken)",
              padding: "10px 16px",
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {section.Name}
          </div>
          <div style={{ padding: 16 }}>
            {(section.items || []).map((item: any) => (
              <div
                key={item.Id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid var(--border-subtle)",
                }}
              >
                <div style={{ flex: 1, paddingRight: 16 }}>
                  <div style={{ fontWeight: 500 }}>{item.Name}</div>
                  {item.Description && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {item.Description}
                    </div>
                  )}
                </div>
                <div>
                  <ItemInputFactory
                    itemType={item.ItemType}
                    options={
                      typeof item.Options === "string"
                        ? JSON.parse(item.Options)
                        : item.Options
                    }
                    ratingScaleOptions={resolveRatingScale(item, section)}
                    value={values[item.Id] ?? null}
                    onChange={(v) =>
                      setValues((prev) => ({ ...prev, [item.Id]: v }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Remarks */}
      <div
        style={{
          border: "1px solid var(--border-default)",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <Form.Label>
          <strong>Overall Remarks</strong>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Teacher's overall remarks for this term..."
        />
      </div>
    </div>
  );
};

export default PerStudentForm;
